import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Dayjs } from 'dayjs';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  getDayOfWeek,
  getPatientIcon,
  getToday,
  isToday,
} from 'src/common/helper';
import { PaginationType } from 'src/components/common/Pagin';
import { postAPI } from 'src/utils/api';
import { getToken, getUserLogged } from 'src/utils/auth';
import BtnOutlineGradient from '../common/button/BtnOutlineGradient';
import CalendarPicker from './partials/CalendarPicker';
import ConfirmBack from './ConfirmBack';
import DialogDetail from './Dialogdetail';
import EditNote from './partials/EditNote';
import { Questionaire } from 'src/common/keyText';

const ReceptionList = () => {
  const [patients, setPatients] = useState([]);
  const [checkAPI, setCheckAPI] = useState(false);
  const [pagination, setPagination] = useState<PaginationType>();
  const [showDialogDetail, setShowDialogDetail] = useState(false);
  const [patientDetail, setPatientDetail] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [selectDate, setSelectDate] = useState(getToday());
  const [dayOfWeek, setDayOfWeek] = useState(getDayOfWeek());
  const statuses = getUserLogged()?.reservestatus || [];
  const [openConfirm, setOpenConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleDayClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setAnchorEl(null);
  };

  const checkWarning = (status: string, visittime: string = '') => {
    if (status !== '予約確定' && status !== '仮予約') return false;
    const date = moment();
    const currentDate = date.format('YYYY/MM/DD');
    if (selectDate > currentDate) {
      return false;
    }
    if (!visittime) return true;
    if (selectDate < currentDate) {
      return true;
    }
    if (selectDate === currentDate) {
      const hour = date.format('HH:mm');
      if (visittime < hour) {
        return true;
      }
    }
    return false;
  };

  const getPatientList = () => {
    const token = getToken();
    postAPI(`/patient/search?token=${token}`, {
      page: 1,
      pagesize: 100,
      day: selectDate,
    }).then((res) => {
      if (res.code === 200) {
        setPatients(res.data.items);
        setPagination(res.data.pagination);
      }
    });
  };

  useEffect(() => {
    setCheckAPI(false);
    getPatientList();
    setCheckAPI(true);
    // eslint-disable-next-line
  }, [selectDate]);

  const handleStatusChange = (
    event: SelectChangeEvent,
    id: string,
    reserveid: string
  ) => {
    const currentStatus = getCurrentStatus(event.target.value);
    localStorage.setItem(
      'statusChange',
      JSON.stringify({
        patientid: id,
        id: reserveid,
        status: currentStatus,
      })
    );
    try {
      const token = getToken();
      postAPI(`/reserve/set-status?token=${token}`, {
        checkpayment: 1,
        patientid: id,
        id: reserveid,
        status: currentStatus?.id || null,
      }).then((res) => {
        if (res.code === 200) {
          localStorage.setItem(
            'statusChange',
            JSON.stringify({
              patientid: 0,
              id: 0,
              status: null,
            })
          );
          getPatientList();
        }
        if (res.code === 501) {
          handleBackClick();
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleRowsClick = (id: number) => {
    const patient = patients.filter((pat: any) => pat.id === id);
    setPatientDetail(patient[0]);
    setShowDialogDetail(true);
  };

  const handleCloseDialogDetail = () => {
    setShowDialogDetail(false);
  };

  const getCurrentStatus = (stt: string) => {
    const currentStatus = statuses.filter(
      (status: any) => status.status === stt
    );
    return currentStatus.length > 0 ? currentStatus[0] : null;
  };

  const onEditNoteClick = (id: string) => {
    setEditNote(id);
  };

  const handleSaveEditNote = () => {
    setEditNote('');
    getPatientList();
  };

  const handleCancelEditNote = () => {
    setEditNote('');
  };

  const handleReturnToday = () => {
    setSelectDate(getToday());
    setDayOfWeek(getDayOfWeek());
  };

  const selectPreviousDay = () => {
    let prevDay = moment(new Date(selectDate));
    prevDay.subtract(1, 'days');
    setSelectDate(prevDay.format('YYYY/MM/DD'));
    setDayOfWeek(prevDay.format('dddd'));
  };

  const selectNextDay = () => {
    let nextDay = moment(new Date(selectDate));
    nextDay.add(1, 'days');
    setSelectDate(nextDay.format('YYYY/MM/DD'));
    setDayOfWeek(nextDay.format('dddd'));
  };

  const handleCloseConfirmBack = () => {
    setOpenConfirm(false);
  };

  const handleBackClick = () => {
    setOpenConfirm(true);
  };

  const handleBack = () => {
    try {
      const token = getToken();
      const statusChange = JSON.parse(
        localStorage.getItem('statusChange') || '""'
      );
      postAPI(`/reserve/set-status?token=${token}`, {
        checkpayment: 0,
        patientid: statusChange.patientid,
        id: statusChange.id,
        status: statusChange.status?.id || null,
      }).then((res) => {
        if (res.code === 200) {
          localStorage.setItem(
            'statusChange',
            JSON.stringify({
              patientid: 0,
              id: 0,
              status: null,
            })
          );
          getPatientList();
          setOpenConfirm(false);
        }
        if (res.code === 501) {
          handleBackClick();
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeDate = (date: Dayjs | null) => {
    if (date) {
      handleCloseCalendar();
      const changeDate = moment(
        new Date(
          `${date.get('year')}/${date.get('month') + 1}/${date.get('date')}`
        )
      );
      setSelectDate(changeDate.format('YYYY/MM/DD'));
      setDayOfWeek(changeDate.format('dddd'));
    }
  };

  return (
    <>
      <Stack direction={'row'} alignItems={'center'} height={'58px'}>
        {!isToday(selectDate) ? (
          <BtnOutlineGradient
            text='本日に戻る'
            handleButtonClick={handleReturnToday}
          />
        ) : (
          <>
            <Typography
              fontSize={'20px'}
              fontWeight={500}
              color={'primary.dark'}
              mt={'18px'}
              mb={'16px'}
              lineHeight={1.2}
            >
              本日すべて
            </Typography>
            <Typography
              fontSize={'14px'}
              fontWeight={700}
              color={'primary.dark'}
              mt={'18px'}
              mb={'16px'}
            >
              ({(pagination && pagination.total) || 0})
            </Typography>
          </>
        )}
        <IconButton onClick={selectPreviousDay} title='Previous day'>
          <img
            src='/images/ic-arrow-left-gray.svg'
            style={{ width: '24px', height: '24px' }}
            alt='icon'
          />
        </IconButton>
        <Typography
          fontSize={'14px'}
          fontWeight={700}
          color={'primary.dark'}
          sx={{
            cursor: 'pointer',
            px: '8px',
            borderRadius: '4px',
            '&.MuiTypography-root:hover': { bgcolor: '#bdbdbd' },
          }}
          onClick={handleDayClick}
        >
          {selectDate}({dayOfWeek})
        </Typography>
        <Popover
          id={'calendar-popover'}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseCalendar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: -10,
            horizontal: 'left',
          }}
        >
          <CalendarPicker
            onChangeDate={handleChangeDate}
            initialValue={selectDate}
          />
        </Popover>
        <IconButton onClick={selectNextDay} title='Next day'>
          <img
            src='/images/ic-arrow-right-gray.svg'
            style={{ width: '24px', height: '24px' }}
            alt='icon'
          />
        </IconButton>
      </Stack>
      <Stack direction={'row'} spacing={'20px'} width={'auto'}>
        <Stack width={'100%'}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
            }}
          >
            <Table
              sx={{
                minWidth: 650,
                '& .MuiTableCell-root.MuiTableCell-head': {
                  borderBottom: '1px solid #F3F3F3',
                  fontWeight: 700,
                  p: '6px 16px',
                  fontSize: '14px',
                },
                '& .MuiTableCell-root.MuiTableCell-body': {
                  color: 'primary.dark',
                  fontSize: '14px',
                  py: '6px',
                },
              }}
              aria-label='simple table'
            >
              <TableHead>
                <TableRow>
                  <TableCell>患者ID</TableCell>
                  <TableCell>患者名</TableCell>
                  <TableCell>受付メモ</TableCell>
                  <TableCell>医師</TableCell>
                  <TableCell>対応内容</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>{Questionaire.Title}</TableCell>
                  <TableCell>来院時間</TableCell>
                  <TableCell>開始時間</TableCell>
                  <TableCell>前回来院日</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkAPI ? (
                  <>
                    {patients.map((row: any, index) => (
                      <TableRow
                        hover
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '& .MuiTableCell-root.MuiTableCell-body': {
                            py: '6px',
                            backgroundColor: checkWarning(
                              row.status,
                              row.visittime
                            )
                              ? '#f7f3e4'
                              : 'primary.light',
                            color: `${
                              row.status === 'キャンセル' ? '#CCD1D9' : ''
                            }`,
                          },
                          cursor: 'pointer',
                        }}
                      >
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          <Stack direction={'row'} spacing={'4px'}>
                            {row.alert && (
                              <Tooltip title={row.alert} arrow>
                                <WarningAmberIcon
                                  sx={{
                                    width: '20px',
                                    height: '20px',
                                    color: '#F6BB42',
                                  }}
                                />
                              </Tooltip>
                            )}
                            {row.patientno}
                          </Stack>
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          <Stack
                            direction={'row'}
                            alignItems={'center'}
                            spacing={'4px'}
                          >
                            <Stack>
                              <Typography fontSize={'10px'}>
                                {row.kana}
                              </Typography>
                              <Typography fontSize={'12px'}>
                                {row.fullname}
                              </Typography>
                            </Stack>
                            {row.firstofmulti === 1 &&
                              row.status !== 'キャンセル' && (
                                <Chip
                                  label='同日予約'
                                  sx={{
                                    borderRadius: '4px',
                                    color: '#DA4453',
                                    border: '1px solid #DA4453',
                                    fontSize: '10px',
                                    height: '21px',
                                    '& .MuiChip-label': {
                                      px: '4px',
                                    },
                                  }}
                                  variant='outlined'
                                />
                              )}
                            {row.first_visit === 1 && (
                              <Chip
                                label='初診'
                                sx={{
                                  borderRadius: '4px',
                                  color: '#364BC1',
                                  border: '1px solid #364BC1',
                                  fontSize: '10px',
                                  height: '21px',
                                  '& .MuiChip-label': {
                                    px: '4px',
                                  },
                                }}
                                variant='outlined'
                              />
                            )}
                          </Stack>
                          <Stack direction={'row'} spacing={'4px'}>
                            {Array.isArray(row.icons) &&
                              row.icons
                                .filter(
                                  (ic: any) =>
                                    ic.piconid !== 3 && ic.piconid !== 7
                                )
                                .map((icon: any, index: number) => {
                                  return (
                                    <Tooltip
                                      key={index}
                                      title={icon.note}
                                      arrow
                                    >
                                      <Box
                                        component='img'
                                        src={getPatientIcon(icon)}
                                        alt={icon.note}
                                      />
                                    </Tooltip>
                                  );
                                })}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction={'row'} spacing={'4px'}>
                            <EditNote
                              visitnote={row.visitnote}
                              id={row.id}
                              reserveid={row.reserveid}
                              onSave={handleSaveEditNote}
                              onEditNoteClick={onEditNoteClick}
                              editNote={editNote}
                              onCancel={handleCancelEditNote}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          {row.doctor}
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          {row.reservetype}
                        </TableCell>
                        <TableCell>
                          <Select
                            className='select-status'
                            value={row.status}
                            onChange={(e) =>
                              handleStatusChange(e, row.id, row.reserveid)
                            }
                            sx={{
                              border: 'none',
                              '& .MuiSelect-select.MuiSelect-outlined': {
                                py: '5px',
                                fontSize: '14px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `#${
                                  getCurrentStatus(row.status)?.bgcolor || ''
                                }`,
                                border: 'none',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  border: 'none',
                                },
                              '&.MuiInputBase-root': {
                                backgroundColor: `#${
                                  getCurrentStatus(row.status)?.bgcolor || ''
                                }`,
                              },
                              '& .MuiSvgIcon-root': {
                                color: `#${
                                  getCurrentStatus(row.status)?.fontcolor || ''
                                }`,
                              },
                              color: `#${
                                getCurrentStatus(row.status)?.fontcolor || ''
                              }`,
                              width: '106px',
                            }}
                          >
                            {statuses.map((status: any, index: number) => {
                              return (
                                <MenuItem key={index} value={status.status}>
                                  {status.status}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)} align='center'>
                          {row.has_answered === 1
                            ? '〇'
                            : row.has_answered === 0
                            ? '✕'
                            : ''}
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          <Stack direction={'row'} spacing={'4px'}>
                            {row.visittime || '-'}{' '}
                            {checkWarning(row.status, row.visittime) && (
                              <Tooltip title={'来院時間が過ぎています'} arrow>
                                <WarningAmberIcon
                                  sx={{
                                    width: '20px',
                                    height: '20px',
                                    color: '#F6BB42',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          {row.time || '-'}
                        </TableCell>
                        <TableCell onClick={() => handleRowsClick(row.id)}>
                          {row.prevtime}
                        </TableCell>
                      </TableRow>
                    ))}
                    {patients.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          sx={{ textAlign: 'center', verticalAlign: 'center' }}
                        >
                          データがありません
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : (
                  <>
                    {Array.from(Array(10)).map((_, index) => {
                      return (
                        <TableRow key={index}>
                          {Array.from(Array(11)).map((_, index) => {
                            return (
                              <TableCell key={index}>
                                <Skeleton
                                  variant='rounded'
                                  width={'100%'}
                                  height={30}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Stack>

      <DialogDetail
        open={showDialogDetail}
        onClose={handleCloseDialogDetail}
        patientDetail={patientDetail}
      />

      <ConfirmBack
        openConfirm={openConfirm}
        handleCloseConfirmDelete={handleCloseConfirmBack}
        handleDelete={handleBack}
      />
    </>
  );
};

export default ReceptionList;
